/*
 * Copyright (c) 2020.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Vladimir Ganturin <gun2rin@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy} from '@angular/core';
import {ParamMap, Router} from '@angular/router';
import {NbDialogService, NbWindowService} from '@nebular/theme';
import {TuiAlertService} from '@taiga-ui/core';
import {EMPTY, Observable, Subject} from 'rxjs';
import {catchError, filter, switchMap, take, tap} from 'rxjs/operators';
import {fadeAnimation} from '../animations/fade.animation';

import {Environment} from '../interfaces/Environment';
import {CompoApiService} from '../services/compo-api.service';
import {ConfirmDialogComponent} from './confirm-dialog/confirm-dialog.component';
import {ErrorDialogComponent} from './error-dialog/error-dialog.component';
import {HttpParams} from "@angular/common/http";

@Component({
    template: '',
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [fadeAnimation],
    standalone: false
})
export abstract class BaseComponent implements OnDestroy {
  loading = false;
  noPhotoSrc = '/assets/images/product/nofoto.jpg';

  protected readonly unsubscribe: Subject<boolean> = new Subject<boolean>();
  protected readonly cdr = inject(ChangeDetectorRef);
  protected readonly dialogService = inject(NbDialogService);
  protected readonly windowService = inject(NbWindowService);
  protected readonly compoApiService = inject(CompoApiService);
  protected readonly router = inject(Router);
  readonly environment = inject(Environment);
  private readonly alerts = inject(TuiAlertService);

  ngOnDestroy() {
    this.unsubscribe.next(true);
    this.unsubscribe.complete();
  }

  successToast(message: string) {
    this.alerts.open(message, {appearance: 'positive', autoClose: this.environment.toastrNotificationDuration}).subscribe();
  }

  warningToast(message: string) {
    this.alerts.open(message, {appearance: 'warning', autoClose: this.environment.toastrNotificationDuration}).subscribe();
  }

  errorToast(message: string) {
    this.alerts.open(message, {appearance: 'negative', autoClose: this.environment.toastrNotificationDuration}).subscribe();
  }

  handleError(err, silent = false) {
    this.loading = false;

    console.error(err);
    this.cdr.detectChanges();
    if (!silent)
    this.dialogService.open(ErrorDialogComponent, {
      context: {
        err: err
      }
    });

    return EMPTY;
  }

  confirm(message: string, callback: Observable<any>) {
    this.dialogService.open(ConfirmDialogComponent, {
      context: {
        description: message
      },
    }).onClose.pipe(
      catchError(err => this.handleError(err)),
      filter(result => result === true),
      switchMap(_ => callback),
      tap(_ => this.cdr?.detectChanges()),
      take(1)
    ).subscribe();
  }

  createLabel(cnt: number, forms: string, withNum = true): string {
    const case1 = (cnt % 100 > 4 && cnt % 100 < 20);
    const case2 = (cnt % 10 < 5) ? cnt % 10 : 5;

    let ret = forms.split('|')[case1 ? 2 : [2, 0, 1, 1, 1, 2][case2]];
    if (withNum) {
      ret = cnt + ' ' + ret;
    }

    return ret;
  }

  get actualRouteParams() {
    return this.router.parseUrl(this.router.url).queryParams;
  }

  paramsToHttp(params: ParamMap) {
    let httpParams = new HttpParams();
    params.keys.forEach(key => {
      httpParams = httpParams.set(key, params.getAll(key).join(','));
    });

    return httpParams;
  }
}
